import { CircleCheck, CircleX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Logo } from '@/components/brand/Logo';
import { Button, Field, Input, LoadingState } from '@/components/ui';
import { api, isApiError } from '@/lib/api';
import { whenLabel } from '@/pages/calendar/time';
import styles from '@/pages/ops/mc.module.css';
import parity from '@/pages/ops/parity.module.css';

interface InviteLookup {
  fullName: string;
  email: string;
  expiresAt: string;
  roles: { role: string; roleName: string; branch: string | null }[];
  mfaRequired: boolean;
  minPasswordLength: number;
}

const UNUSABLE: Record<string, string> = {
  expired: 'This invitation has expired. Ask the clinic to send you a new one.',
  used: 'This invitation has already been used. Sign in with the password you chose.',
  cancelled:
    'This invitation was cancelled. Ask the clinic if you still need access.',
  invalid:
    'This invitation link is not valid. Check you copied the whole link.',
};

/**
 * RL-1034 public invitation acceptance. The token travels in the URL
 * fragment (never sent to the server or written to access logs) and is
 * posted in the request body; the API decides role and branches.
 */
export function AcceptInvitePage() {
  const [token] = useState(() => window.location.hash.replace(/^#/, ''));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  useEffect(() => {
    document.title = 'Accept invitation · Ommri Care';
    // Drop the token from the address bar and history once read.
    if (window.location.hash)
      window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const q = useQuery({
    queryKey: ['invite-lookup', token],
    enabled: token.length > 0,
    retry: false,
    queryFn: ({ signal }) =>
      api
        .post<InviteLookup>('/auth/invites/lookup', { token }, { signal })
        .then((r) => r.data),
  });
  const accept = useMutation({
    mutationFn: () =>
      api
        .post<{ email: string }>('/auth/invites/accept', { token, password })
        .then((r) => r.data),
  });
  const reason = (err: unknown) =>
    isApiError(err) && err.status === 410
      ? String(err.problem?.reason ?? 'invalid')
      : isApiError(err) && err.status === 400
        ? 'invalid'
        : null;
  const lookupReason = token ? reason(q.error) : 'invalid';
  const acceptReason = reason(accept.error);
  const min = q.data?.minPasswordLength ?? 12;
  const mismatch = confirm.length > 0 && confirm !== password;

  let body;
  if (accept.isSuccess) {
    body = (
      <div className={`${styles.vban} ${styles.ok}`} role="status">
        <CircleCheck aria-hidden="true" />
        <div>
          <b>Your account is ready</b>
          <span>
            Sign in as {accept.data.email}.
            {q.data?.mfaRequired
              ? ' You will set up two-factor sign-in first.'
              : ''}{' '}
            <Link to="/sign-in">Go to sign in</Link>
          </span>
        </div>
      </div>
    );
  } else if (lookupReason || acceptReason) {
    body = (
      <div className={`${styles.vban} ${styles.bad}`} role="alert">
        <CircleX aria-hidden="true" />
        <div>
          <b>This invitation link cannot be used</b>
          <span>
            {UNUSABLE[(lookupReason ?? acceptReason)!] ?? UNUSABLE.invalid}
          </span>
        </div>
      </div>
    );
  } else if (q.isLoading) {
    body = <LoadingState />;
  } else if (q.error || !q.data) {
    body = (
      <div className={`${styles.vban} ${styles.bad}`} role="alert">
        <CircleX aria-hidden="true" />
        <div>
          <b>Could not check the invitation</b>
          <span>Try again in a moment.</span>
        </div>
      </div>
    );
  } else {
    const inv = q.data;
    body = (
      <form
        className={parity.form}
        onSubmit={(e) => {
          e.preventDefault();
          accept.mutate();
        }}
      >
        <dl className={parity.kv}>
          <dt>Name</dt>
          <dd>{inv.fullName}</dd>
          <dt>Email</dt>
          <dd>{inv.email}</dd>
          <dt>Access</dt>
          <dd>
            {inv.roles
              .map((r) => `${r.roleName} · ${r.branch ?? 'all branches'}`)
              .join('; ')}
          </dd>
          <dt>Expires</dt>
          <dd>{whenLabel(inv.expiresAt)}</dd>
        </dl>
        <Field
          label="Choose a password"
          hint={`At least ${min} characters, not your email address.`}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field
          label="Confirm password"
          error={mismatch ? 'The passwords do not match.' : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        {accept.error && !acceptReason && (
          <p className={parity.notice} role="alert">
            {isApiError(accept.error)
              ? (accept.error.problem?.detail ?? accept.error.message)
              : 'Could not accept the invitation.'}
          </p>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={accept.isPending}
          disabled={password.length < min || confirm !== password}
        >
          Create my account
        </Button>
      </form>
    );
  }
  return (
    <main className={styles.vpage}>
      <div className={styles.vwrap}>
        <div className={styles.vtop}>
          <Logo />
          <span>Staff invitation</span>
        </div>
        {body}
      </div>
    </main>
  );
}
